import {ref} from "vue";

export let topPaneHeight = ref(50); // Default height of top pane in percentage
export let bottomPaneHeight = ref(50); // Default height of bottom pane in percentage
export let isAutoResizing = false;
let isDragging = false; // Track whether the handle is being dragged


// The divider between the panes. Phone testers reported it "getting stuck";
// two things did that, both guarded by resize-pane.spec.js:
//   1. the drag cap measured the placeholder's direct child, which on phones
//      is height:100% of the pane being measured, so a downward drag was
//      clamped to 12px per touchmove (it lagged the finger and then stopped);
//   2. touchcancel was never handled. iOS fires it when its own pan wins the
//      gesture (or on an edge swipe), so isDragging latched true forever: the
//      document-level touchmove kept preventDefault-ing (nothing scrolled)
//      and every auto-fit early-returned. Only a reload cleared it.
export const isDraggingNow = () => isDragging;

export const startDragging = (event) => {
  isDragging = true;
  isAutoResizing = false;
  const type = (event && event.type) || (typeof window !== 'undefined' && window.event && window.event.type) || 'mousedown';
  if (type === 'touchstart') {
    document.addEventListener("touchmove", resizePanes, { passive: false });
    document.addEventListener("touchend", stopDragging, { passive: false });
    document.addEventListener("touchcancel", stopDragging, { passive: false });
  } else {
    document.addEventListener("mousemove", resizePanes);
    document.addEventListener("mouseup", stopDragging);
  }
  window.addEventListener("blur", stopDragging);
};

const stopDragging = () => {
  isDragging = false;
  document.removeEventListener("mousemove", resizePanes);
  document.removeEventListener("mouseup", stopDragging);
  document.removeEventListener("touchmove", resizePanes);
  document.removeEventListener("touchend", stopDragging);
  document.removeEventListener("touchcancel", stopDragging);
  window.removeEventListener("blur", stopDragging);
};

// Test hook: drop any latched drag between cases.
export const _resetForTests = () => { stopDragging(); isAutoResizing = false; };

// What the top pane is showing, in pixels — the reading element itself, or
// null when there is no natural-height content to cap against. The card deck
// (.select-cards-component) and the weaving screen (.srl-wrap > .srl-loading)
// are height:100% of the pane, so measuring them (or any placeholder child as
// a fallback) made the cap track the divider: pane − 14 + 12 = a downward drag
// that shrank the pane 2px per touchmove. Reproduced on an iPhone SE 2026-09-27.
const topPaneContentHeight = (ph) => {
  const modal = ph.querySelector(".spread-reading, .srl-response, .one-card-response, .four-card-reading");
  return modal ? modal.getBoundingClientRect().height : 0;
};

const resizePanes = (event) => {
  if (!isDragging) return;

  let clientY;
  if (event.type === 'touchmove') {
    if (event.cancelable) event.preventDefault();   // the divider owns this gesture
    clientY = event.touches[0].clientY;
  } else {
    clientY = event.clientY;
  }

  const container = document.querySelector(".container");
  if (!container) return;
  const containerRect = container.getBoundingClientRect();
  if (!containerRect.height) return;
  let newTopHeight = ((clientY - containerRect.top) / containerRect.height) * 100;

  // Cap the drag at the content of the top pane so the divider can't open an
  // empty gap below the reading.
  // The cap holds the divider, it never pulls it up: a cap below the current
  // height (content still streaming, a stale measurement) would otherwise turn
  // a downward drag into an upward one.
  const ph = document.querySelector(".action-placeholder");
  if (ph) {
    const contentH = topPaneContentHeight(ph);
    if (contentH > 0) {
      const capPct = Math.max(((contentH + 12) / containerRect.height) * 100, topPaneHeight.value);
      if (newTopHeight > capPct) newTopHeight = capPct;
    }
  }

  // Clamp (never `return`): past the edge the divider stays at the edge and
  // keeps tracking, instead of freezing until the finger comes back in range.
  newTopHeight = Math.max(10, Math.min(90, newTopHeight));

  topPaneHeight.value = newTopHeight;
  bottomPaneHeight.value = 100 - newTopHeight;
};


// One-shot fit. Called by reading components after they render (e.g.,
// OneCardReading mounted hook on Continue-load) so the divider lands
// right under the rendered content instead of leaving a black gap. No
// observers — fires once, then the user owns the drag thereafter.
// Skipped if the user is mid-drag; otherwise shrinks the top pane to
// match the tallest child of `.action-placeholder` plus a small pad.
export const fitTopPaneToContentOnce = () => {
  if (isDragging) return;
  const container = document.querySelector(".container");
  const ph = document.querySelector(".action-placeholder");
  if (!container || !ph) return;
  let contentH = 0;
  for (const c of ph.children) {
    const r = c.getBoundingClientRect();
    if (r.height > contentH) contentH = r.height;
  }
  if (!contentH) return;
  const containerH = container.getBoundingClientRect().height;
  if (!containerH) return;
  const desired = ((contentH + 12) / containerH) * 100;
  // Only shrink — never grow. If the preset is already smaller than the
  // content, the content can still overflow and scroll inside the pane.
  if (desired >= topPaneHeight.value) return;
  // Floor + cap so neither pane can be crushed.
  const next = Math.max(25, Math.min(82, desired));
  topPaneHeight.value = next;
  bottomPaneHeight.value = 100 - next;
};

// Fit the top pane to its content — grow OR shrink — so the divider sits
// right below the rendered content with no black gap (and no overflow).
// Unlike fitTopPaneToContentOnce (shrink-only), this is used when a reading
// renders: its height varies with the streamed text + the illustration, so a
// fixed % would leave a black gap below short content or clip tall content.
// Capped so neither pane is crushed.
// Desktop caps the reading pane at 68 so the chat lands tall enough (≈32%) to
// show the post-reading bar AND the inline share-channel row without any later
// resize; mobile keeps 72 so the chat doesn't get crushed.
export const fitTopPaneToContent = (
  minPct = 34,
  // Phones cap lower: at 72 the chat under the reading was ~160px tall.
  // 700 is the CSS desktop line; the JS used to switch at 800, so 700-799px
  // devices got desktop layout with phone pane math.
  maxPct = (typeof window !== 'undefined' && window.innerWidth >= 700) ? 68 : 62,
) => {
  if (isDragging) return;
  const container = document.querySelector(".container");
  const ph = document.querySelector(".action-placeholder");
  if (!container || !ph) return;
  // Measure the actual reading modal (not the placeholder's child wrapper,
  // which can be taller than the visible content and inflate the fit,
  // leaving a black gap). Fall back to the tallest child otherwise.
  let contentH = 0;
  const modal = ph.querySelector(".spread-reading, .srl-response, .one-card-response, .four-card-reading");
  if (modal) {
    contentH = modal.getBoundingClientRect().height;
  } else {
    for (const c of ph.children) {
      const r = c.getBoundingClientRect();
      if (r.height > contentH) contentH = r.height;
    }
  }
  if (!contentH) return;
  const containerH = container.getBoundingClientRect().height;
  if (!containerH) return;
  const desired = ((contentH + 12) / containerH) * 100;
  const next = Math.max(minPct, Math.min(maxPct, desired));
  topPaneHeight.value = next;
  bottomPaneHeight.value = 100 - next;
};

// About/Doorman entry fit: size the BOTTOM (action) pane to just hold its
// content — the Doorman menu buttons (and any intro line) — framed with a
// `margin` (default 20px) above and below the buttons, handing the rest to the
// top (About) pane so the reader's description fits without an internal
// scrollbar. Only meaningful once the Doorman menu buttons (.qa-cards) are
// rendered; returns true when it successfully fit, false otherwise (so callers
// can retry while the buttons stream in).
export const fitActionPaneToButtons = (margin = 20) => {
  if (isDragging) return false;
  const container = document.querySelector(".container");
  const pane = document.querySelector(".pane-bottom");
  const cards = document.querySelector(".pane-bottom .qa-cards");
  if (!container || !pane || !cards) return false;
  const containerH = container.getBoundingClientRect().height;
  if (!containerH) return false;
  // The action content runs from the top of the chat content to the bottom of
  // the buttons. Content is top-aligned, so this is stable regardless of the
  // pane's current height. Frame it with `margin` top & bottom.
  const scroller = pane.querySelector(".chat-box-container") || pane;
  const first = scroller.firstElementChild || scroller;
  const contentTop = first.getBoundingClientRect().top;
  const contentBottom = cards.getBoundingClientRect().bottom;
  const contentH = contentBottom - contentTop;
  if (contentH <= 0) return false;
  const desiredPx = contentH + margin * 2;
  let pct = (desiredPx / containerH) * 100;
  // Keep both panes sane — never crush the chat, never starve the About pane.
  pct = Math.max(16, Math.min(52, pct));
  bottomPaneHeight.value = pct;
  topPaneHeight.value = 100 - pct;
  return true;
};

export const adjustPanesForChat = (chatContentHeight) => {
  if (isDragging) return; // Prevent conflicts with manual resizing

  const container = document.querySelector(".container");
  const containerHeight = container ? container.getBoundingClientRect().height : 0;

  if (!containerHeight) return;

  const newBottomPaneHeight = (chatContentHeight / containerHeight) * 100;

  // Cap the bottom pane at 20% height
  if (newBottomPaneHeight <= 20 && newBottomPaneHeight > bottomPaneHeight.value) {
    isAutoResizing = true;
    bottomPaneHeight.value = newBottomPaneHeight;
    topPaneHeight.value = 100 - newBottomPaneHeight;

    // Stop auto-resizing after a brief delay
    setTimeout(() => {
      isAutoResizing = false;
    }, 300);
  }
};